import { useState } from 'react';

// ── True/False ────────────────────────────────────────────
const TrueFalse = ({ data, translatedQuestions }) => {
  const questions = translatedQuestions || data.questions;
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const handleAnswer = (idx, val) => { if (submitted) return; setAnswers(prev => ({ ...prev, [idx]: val })); };
  const score = submitted ? questions.filter((q, i) => answers[i] === q.answer).length : 0;
  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--brand-primary)', textTransform: 'uppercase', marginBottom: '12px' }}>✋ True or False?</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {questions.map((q, i) => {
          const isCorrect = submitted && answers[i] === q.answer;
          const isWrong = submitted && answers[i] !== undefined && answers[i] !== q.answer;
          return (
            <div key={i} style={{ background: submitted ? (isCorrect ? '#E6F4EC' : isWrong ? '#FEE2E2' : 'var(--bg)') : 'var(--bg)', border: `1.5px solid ${isCorrect ? '#1B7F3E' : isWrong ? '#DC2626' : 'var(--border)'}`, borderRadius: '10px', padding: '12px 14px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', lineHeight: 1.5 }}>{q.q}</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[true, false].map(val => (
                  <button key={String(val)} onClick={() => handleAnswer(i, val)}
                    style={{ flex: 1, padding: '8px', borderRadius: '8px', fontSize: '12px', fontWeight: 800, cursor: submitted ? 'default' : 'pointer', border: answers[i] === val ? 'none' : '1.5px solid var(--border)', background: answers[i] === val ? (val ? '#1B7F3E' : '#DC2626') : 'white', color: answers[i] === val ? 'white' : 'var(--muted)' }}>
                    {val ? '✓ True' : '✕ False'}
                  </button>
                ))}
              </div>
              {submitted && <div style={{ marginTop: '8px', fontSize: '11px', fontWeight: 700, color: isCorrect ? '#1B7F3E' : '#DC2626' }}>{isCorrect ? '✅ Correct!' : `❌ Answer: ${q.answer ? 'True' : 'False'}`}</div>}
            </div>
          );
        })}
      </div>
      {!submitted && Object.keys(answers).length === questions.length && (
        <button onClick={() => setSubmitted(true)} style={{ width: '100%', marginTop: '12px', background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '10px', padding: '11px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>Check Answers</button>
      )}
      {submitted && (
        <div style={{ marginTop: '12px', background: score === data.questions.length ? '#E6F4EC' : '#FEF3C7', borderRadius: '10px', padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: 800, color: score === data.questions.length ? '#1B7F3E' : '#7C2D12' }}>
          {score}/{questions.length} correct {score === data.questions.length ? '🎉 Perfect!' : '— Keep practicing!'}
        </div>
      )}
    </div>
  );
};

// ── Drag & Drop ───────────────────────────────────────────
const DragDrop = ({ data }) => {
  const [bins, setBins] = useState(() => {
    const b = {};
    data.bins.forEach(bin => { b[bin] = []; });
    return b;
  });
  const [unplaced, setUnplaced] = useState(data.items || []);
  const [checked, setChecked] = useState(false);
  const [dragging, setDragging] = useState(null);

  const handleDrop = (binName) => {
    if (!dragging || checked) return;
    if (dragging.source === 'bin') {
      setBins(prev => ({ ...prev, [dragging.sourceBin]: prev[dragging.sourceBin].filter(i => i.id !== dragging.item.id), [binName]: [...prev[binName], dragging.item] }));
    } else {
      setUnplaced(prev => prev.filter(i => i.id !== dragging.item.id));
      setBins(prev => ({ ...prev, [binName]: [...prev[binName], dragging.item] }));
    }
    setDragging(null);
  };

  const handleDropUnplaced = () => {
    if (!dragging || dragging.source !== 'bin' || checked) return;
    setBins(prev => ({ ...prev, [dragging.sourceBin]: prev[dragging.sourceBin].filter(i => i.id !== dragging.item.id) }));
    setUnplaced(prev => [...prev, dragging.item]);
    setDragging(null);
  };

  const totalPlaced = Object.values(bins).flat().length;
  const totalItems = data.items?.length || 0;
  const correct = checked ? Object.entries(bins).reduce((acc, [binName, items]) => acc + items.filter(i => i.correct_bin === binName).length, 0) : 0;

  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--brand-primary)', textTransform: 'uppercase', marginBottom: '12px' }}>🃏 Drag & Drop</div>

      {/* Unplaced items */}
      <div onDragOver={e => e.preventDefault()} onDrop={handleDropUnplaced}
        style={{ minHeight: '60px', background: 'var(--bg)', border: '2px dashed var(--border)', borderRadius: '10px', padding: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
        {unplaced.length === 0 && <div style={{ fontSize: '12px', color: 'var(--muted)', width: '100%', textAlign: 'center', padding: '8px' }}>All items placed!</div>}
        {unplaced.map(item => (
          <div key={item.id} draggable onDragStart={() => setDragging({ item, source: 'unplaced' })}
            style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '8px', padding: '7px 12px', fontSize: '12px', fontWeight: 600, cursor: 'grab', userSelect: 'none' }}>
            {item.text}
          </div>
        ))}
      </div>

      {/* Bins */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${data.bins.length}, 1fr)`, gap: '10px', marginBottom: '12px' }}>
        {data.bins.map(binName => (
          <div key={binName} onDragOver={e => e.preventDefault()} onDrop={() => handleDrop(binName)}
            style={{ minHeight: '100px', background: '#F7F8FC', border: '2px dashed #CBD5E1', borderRadius: '10px', padding: '10px' }}>
            <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--brand-secondary)', textTransform: 'uppercase', marginBottom: '8px', textAlign: 'center' }}>{binName}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {bins[binName].map(item => {
                const isCorrect = checked && item.correct_bin === binName;
                const isWrong = checked && item.correct_bin !== binName;
                return (
                  <div key={item.id} draggable onDragStart={() => setDragging({ item, source: 'bin', sourceBin: binName })}
                    style={{ background: checked ? (isCorrect ? '#E6F4EC' : '#FEE2E2') : 'white', border: `1.5px solid ${checked ? (isCorrect ? '#1B7F3E' : '#DC2626') : 'var(--border)'}`, borderRadius: '8px', padding: '6px 10px', fontSize: '12px', fontWeight: 600, cursor: checked ? 'default' : 'grab' }}>
                    {item.text} {checked && (isCorrect ? '✓' : '✗')}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {!checked && totalPlaced === totalItems && (
        <button onClick={() => setChecked(true)} style={{ width: '100%', background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '10px', padding: '11px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>Check Answers</button>
      )}
      {checked && (
        <div style={{ background: correct === totalItems ? '#E6F4EC' : '#FEF3C7', borderRadius: '10px', padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: 800, color: correct === totalItems ? '#1B7F3E' : '#7C2D12' }}>
          {correct}/{totalItems} correct! {correct === totalItems ? '🎉 Perfect!' : 'Try again!'}
        </div>
      )}
    </div>
  );
};

// ── Fill in the Blanks ────────────────────────────────────
const FillBlank = ({ data }) => {
  const [inputs, setInputs] = useState(() => data.sentences.map(s => s.blanks.map(() => '')));
  const [checked, setChecked] = useState(false);

  const handleInput = (si, bi, val) => {
    if (checked) return;
    setInputs(prev => prev.map((s, i) => i === si ? s.map((b, j) => j === bi ? val : b) : s));
  };

  const allFilled = inputs.every(s => s.every(b => b.trim() !== ''));
  const score = checked ? data.sentences.reduce((acc, s, si) =>
    acc + s.blanks.filter((b, bi) => inputs[si][bi].trim().toLowerCase() === b.toLowerCase()).length, 0) : 0;
  const total = data.sentences.reduce((acc, s) => acc + s.blanks.length, 0);

  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--brand-primary)', textTransform: 'uppercase', marginBottom: '12px' }}>✏️ Fill in the Blanks</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {data.sentences.map((s, si) => {
          const parts = s.text.split('___');
          return (
            <div key={si} style={{ background: 'var(--bg)', border: '1.5px solid var(--border)', borderRadius: '10px', padding: '12px 14px', fontSize: '13px', lineHeight: 2 }}>
              {parts.map((part, pi) => (
                <span key={pi}>
                  {part}
                  {pi < parts.length - 1 && (
                    <span style={{ position: 'relative', display: 'inline-block' }}>
                      <input value={inputs[si]?.[pi] || ''} onChange={e => handleInput(si, pi, e.target.value)}
                        style={{ width: '100px', borderBottom: `2px solid ${checked ? (inputs[si]?.[pi]?.trim().toLowerCase() === s.blanks[pi]?.toLowerCase() ? '#1B7F3E' : '#DC2626') : 'var(--brand-primary)'}`, border: 'none', borderBottom: `2px solid ${checked ? (inputs[si]?.[pi]?.trim().toLowerCase() === s.blanks[pi]?.toLowerCase() ? '#1B7F3E' : '#DC2626') : 'var(--brand-primary)'}`, background: 'transparent', outline: 'none', fontSize: '13px', fontWeight: 700, color: 'var(--text)', textAlign: 'center', padding: '0 4px', margin: '0 4px' }} />
                      {checked && inputs[si]?.[pi]?.trim().toLowerCase() !== s.blanks[pi]?.toLowerCase() && (
                        <span style={{ fontSize: '11px', color: '#1B7F3E', fontWeight: 700, marginLeft: '4px' }}>({s.blanks[pi]})</span>
                      )}
                    </span>
                  )}
                </span>
              ))}
            </div>
          );
        })}
      </div>
      {!checked && allFilled && (
        <button onClick={() => setChecked(true)} style={{ width: '100%', marginTop: '12px', background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '10px', padding: '11px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>Check Answers</button>
      )}
      {checked && (
        <div style={{ marginTop: '12px', background: score === total ? '#E6F4EC' : '#FEF3C7', borderRadius: '10px', padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: 800, color: score === total ? '#1B7F3E' : '#7C2D12' }}>
          {score}/{total} correct! {score === total ? '🎉 Perfect!' : '— Check the answers above!'}
        </div>
      )}
    </div>
  );
};

// ── Label Diagram (activity card for now) ─────────────────
const ActivityCard = ({ data, icon, title }) => (
  <div style={{ background: 'var(--brand-secondary-light)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 16px' }}>
    <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--brand-secondary)', textTransform: 'uppercase', marginBottom: '8px' }}>{icon} {title}</div>
    <div style={{ fontSize: '13px', color: 'var(--text)', lineHeight: 1.6 }}>{data.description}</div>
    <div style={{ marginTop: '10px', fontSize: '11px', color: 'var(--muted)', fontWeight: 600, fontStyle: 'italic' }}>Interactive version coming soon!</div>
  </div>
);

// ── Main Component ────────────────────────────────────────
const InteractiveBlock = ({ data, translatedQuestions }) => {
  if (!data || !data.type) return null;
  switch (data.type) {
    case 'true_false': return <TrueFalse data={data} translatedQuestions={translatedQuestions} />;
    case 'drag_drop': return data.items ? <DragDrop data={data} /> : <ActivityCard data={data} icon="🃏" title="Drag and Drop Activity" />;
    case 'fill_blank': return data.sentences ? <FillBlank data={data} /> : <ActivityCard data={data} icon="✏️" title="Fill in the Blanks" />;
    case 'label_diagram': return <ActivityCard data={data} icon="🏷️" title="Label the Diagram" />;
    default: return <ActivityCard data={data} icon="🎯" title="Activity" />;
  }
};

export default InteractiveBlock;
